import { useLocalParticipant, useRoomContext } from '@livekit/components-react'
import { useCallback, useEffect, useState } from 'react'

const VIRTUAL_AUDIO_DEVICES = ['blackhole', 'soundflower', 'loopback']

function isVirtualAudioDevice(device: MediaDeviceInfo): boolean {
  const label = device.label.toLowerCase()
  return VIRTUAL_AUDIO_DEVICES.some((name) => label.includes(name))
}

function useDeviceList() {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])

  const enumerate = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices()
    setAudioDevices(devices.filter((d) => d.kind === 'audioinput' && !isVirtualAudioDevice(d)))
    setVideoDevices(devices.filter((d) => d.kind === 'videoinput'))
  }, [])

  useEffect(() => {
    enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [enumerate])

  return { audioDevices, videoDevices, enumerate }
}

/**
 * Pre-join device management: requests permissions, enumerates devices, auto-selects first available.
 */
export function useMediaDevices() {
  const { audioDevices, videoDevices, enumerate } = useDeviceList()
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('')
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('')

  // Request permissions on mount to get labeled devices, then re-enumerate
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .catch(() => {})
      .then(() => enumerate())
  }, [enumerate])

  // Auto-select first available device
  useEffect(() => {
    const first = audioDevices[0]
    if (!selectedAudioDevice && first) {
      setSelectedAudioDevice(first.deviceId)
    }
  }, [audioDevices, selectedAudioDevice])

  useEffect(() => {
    const first = videoDevices[0]
    if (!selectedVideoDevice && first) {
      setSelectedVideoDevice(first.deviceId)
    }
  }, [videoDevices, selectedVideoDevice])

  return {
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
  }
}

function getDeviceId(track: ReturnType<typeof useLocalParticipant>['microphoneTrack']): string {
  return track?.track?.mediaStreamTrack?.getSettings().deviceId ?? ''
}

/**
 * In-room device management: syncs active device from LiveKit tracks, allows switching.
 */
export function useRoomDevices() {
  const room = useRoomContext()
  const { microphoneTrack, cameraTrack } = useLocalParticipant()
  const { audioDevices, videoDevices } = useDeviceList()
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('')
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('')

  // Sync initial device IDs from tracks when they become available
  useEffect(() => {
    const id = getDeviceId(microphoneTrack)
    if (id) setSelectedAudioDevice(id)
  }, [microphoneTrack])

  useEffect(() => {
    const id = getDeviceId(cameraTrack)
    if (id) setSelectedVideoDevice(id)
  }, [cameraTrack])

  const switchAudioDevice = useCallback(
    async (deviceId: string) => {
      setSelectedAudioDevice(deviceId)
      await room.switchActiveDevice('audioinput', deviceId)
    },
    [room]
  )

  const switchVideoDevice = useCallback(
    async (deviceId: string) => {
      setSelectedVideoDevice(deviceId)
      await room.switchActiveDevice('videoinput', deviceId)
    },
    [room]
  )

  return {
    audioDevices,
    videoDevices,
    selectedAudioDevice,
    selectedVideoDevice,
    switchAudioDevice,
    switchVideoDevice,
  }
}
